import { AddOutlined, DeleteOutlined } from '@mui/icons-material';
import { Alert, Box, Button, IconButton, MenuItem, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type {
  ContactChange,
  ContactChangeInput,
  ContactScope,
  PersonAddress,
  PersonEmailAddress,
  PersonPhoneNumber,
  WorkdayReference,
  WorkerPrivateProfile,
  WorkerProfileWorker,
} from './model';
import { ChoiceControl, FormDialog, FormTextField, ValueBadge, createFocusRingStyles, designTokens } from '../../../design-system';
import type { ContactChangePort } from './port';

type ContactPhoneUsage = NonNullable<ContactChangeInput['phones']>[number]['usageType'];

type ContactPhoneDraft = {
  key: string;
  phoneNumber: string;
  extension: string;
  usageType: ContactPhoneUsage;
  primary: boolean;
  public: boolean;
};

type EmergencyContactDraft = {
  key: string;
  name: string;
  relationshipType: string;
  phoneNumbers: ContactPhoneDraft[];
  emailAddresses: Array<{
    key: string;
    emailAddress: string;
    usageType: 'PERSONAL' | 'WORK' | 'OTHER';
  }>;
};

type ContactScopeDraft = {
  email: string;
  emailPublic: boolean;
  phones: ContactPhoneDraft[];
  address: string;
  city: string;
  region: string;
  postalCode: string;
  emergencyContacts: EmergencyContactDraft[];
  emailDirty: boolean;
  phonesDirty: boolean;
  addressDirty: boolean;
  emergencyContactsDirty: boolean;
};

type ContactSubmissionOperation = {
  signature: string;
  createKey: string;
  submitKey: string;
  draft?: Pick<ContactChange, 'id' | 'scope'>;
};

export type ContactChangeWorkflowReference = {
  scope: ContactScope;
  workflow: WorkdayReference;
};

type ContactSection = ContactScope | 'EMERGENCY';

type ContactChangeDialogProps = {
  open: boolean;
  initialSection?: ContactSection;
  port: ContactChangePort;
  worker: WorkerProfileWorker;
  profile: WorkerPrivateProfile;
  fixtureMode: boolean;
  onClose: () => void;
  onSubmitted: (message: string, workflows?: readonly ContactChangeWorkflowReference[]) => void;
};

const homePhoneTypes: Array<{ value: ContactPhoneUsage; label: string }> = [
  { value: 'MOBILE', label: '휴대전화' },
  { value: 'HOME', label: '자택 전화' },
  { value: 'OTHER', label: '기타' },
];

const workPhoneTypes: Array<{ value: ContactPhoneUsage; label: string }> = [
  { value: 'WORK', label: '업무 전화' },
  { value: 'MOBILE', label: '업무용 휴대전화' },
  { value: 'FAX', label: '팩스' },
  { value: 'OTHER', label: '기타' },
];

const emergencyRelationshipTypes = [
  { value: 'SPOUSE', label: '배우자' },
  { value: 'PARTNER', label: '파트너' },
  { value: 'PARENT', label: '부모' },
  { value: 'SIBLING', label: '형제·자매' },
  { value: 'CHILD', label: '자녀' },
  { value: 'GUARDIAN', label: '보호자' },
  { value: 'FRIEND', label: '친구' },
  { value: 'COLLEAGUE', label: '동료' },
  { value: 'OTHER', label: '기타' },
] as const;

const emergencyPhoneTypes: Array<{ value: ContactPhoneUsage; label: string }> = [
  { value: 'MOBILE', label: '휴대전화' },
  { value: 'HOME', label: '자택 전화' },
  { value: 'WORK', label: '업무 전화' },
  { value: 'OTHER', label: '기타' },
];

const emergencyEmailTypes = [
  { value: 'PERSONAL', label: '개인 이메일' },
  { value: 'WORK', label: '회사 이메일' },
  { value: 'OTHER', label: '기타' },
] as const;

const maxEmergencyContacts = 20;
const maxEmergencyContactMethods = 10;

function todayInSeoul() {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date());
}

function primaryItem<T extends { primary?: boolean }>(items: T[]) {
  return items.find((item) => item.primary) ?? items[0];
}

function validPhoneUsage(scope: ContactScope, value?: string): ContactPhoneUsage {
  const allowed = scope === 'HOME' ? homePhoneTypes : workPhoneTypes;
  return allowed.some((item) => item.value === value) ? value as ContactPhoneUsage : scope === 'HOME' ? 'MOBILE' : 'WORK';
}

function phoneDrafts(scope: ContactScope, phones: PersonPhoneNumber[]): ContactPhoneDraft[] {
  return phones.map((phone) => ({
    key: phone.id || crypto.randomUUID(),
    phoneNumber: phone.phoneNumber ?? '',
    extension: scope === 'WORK' ? phone.extension ?? '' : '',
    usageType: validPhoneUsage(scope, phone.usageType),
    primary: Boolean(phone.primary),
    public: Boolean(phone.public),
  }));
}

function normalizePrimary<T extends { primary?: boolean }>(items: T[], preferredIndex?: number) {
  if (!items.length) return items;
  const primaryIndex = preferredIndex ?? items.findIndex((item) => item.primary);
  const selectedIndex = primaryIndex >= 0 ? primaryIndex : 0;
  return items.map((item, index) => ({ ...item, primary: index === selectedIndex }));
}

function emailPayload(items: PersonEmailAddress[], value: string, scope: ContactScope, isPublic: boolean): ContactChangeInput['emails'] {
  const selected = primaryItem(items);
  const rows = items
    .filter((item) => item.id !== selected?.id)
    .flatMap((item) => item.emailAddress ? [{
      emailAddress: item.emailAddress,
      usageType: (['HOME', 'PERSONAL', 'WORK', 'OTHER'].includes(item.usageType ?? '') ? item.usageType : scope === 'HOME' ? 'PERSONAL' : 'WORK') as NonNullable<ContactChangeInput['emails']>[number]['usageType'],
      primary: false,
      public: Boolean(item.public),
    }] : []);
  if (value) rows.unshift({
    emailAddress: value,
    usageType: (['HOME', 'PERSONAL', 'WORK', 'OTHER'].includes(selected?.usageType ?? '') ? selected?.usageType : scope === 'HOME' ? 'PERSONAL' : 'WORK') as NonNullable<ContactChangeInput['emails']>[number]['usageType'],
    primary: true,
    public: isPublic,
  });
  return normalizePrimary(rows);
}

function addressPayload(items: PersonAddress[], input: {
  line: string;
  city: string;
  region: string;
  postalCode: string;
}): ContactChangeInput['addresses'] {
  const selected = primaryItem(items);
  const rows = items
    .filter((item) => item.id !== selected?.id)
    .flatMap((item) => item.addressLines?.length ? [{
      countryCode: item.country?.id || 'KR',
      region: item.region,
      city: item.city,
      postalCode: item.postalCode,
      addressLines: item.addressLines,
      usageType: (['HOME', 'MAILING', 'OTHER'].includes(item.usageType ?? '') ? item.usageType : 'HOME') as NonNullable<ContactChangeInput['addresses']>[number]['usageType'],
      primary: false,
      public: Boolean(item.public),
    }] : []);
  if (input.line) rows.unshift({
    countryCode: selected?.country?.id || 'KR',
    region: input.region || undefined,
    city: input.city || undefined,
    postalCode: input.postalCode || undefined,
    addressLines: [input.line],
    usageType: (['HOME', 'MAILING', 'OTHER'].includes(selected?.usageType ?? '') ? selected?.usageType : 'HOME') as NonNullable<ContactChangeInput['addresses']>[number]['usageType'],
    primary: true,
    public: Boolean(selected?.public),
  });
  return normalizePrimary(rows);
}

function emergencyContactDrafts(profile: WorkerPrivateProfile): EmergencyContactDraft[] {
  return (profile.emergencyContacts ?? []).map((contact) => ({
    key: contact.id || crypto.randomUUID(),
    name: contact.name ?? '',
    relationshipType: contact.relationshipType || 'OTHER',
    phoneNumbers: (contact.phoneNumbers ?? []).map((phone) => ({
      key: crypto.randomUUID(),
      phoneNumber: phone.phoneNumber ?? '',
      extension: '',
      usageType: (['MOBILE', 'HOME', 'WORK', 'OTHER'].includes(phone.usageType ?? '') ? phone.usageType : 'MOBILE') as ContactPhoneUsage,
      primary: Boolean(phone.primary),
      public: false,
    })),
    emailAddresses: (contact.emailAddresses ?? []).map((email) => ({
      key: crypto.randomUUID(),
      emailAddress: email.emailAddress ?? '',
      usageType: (['PERSONAL', 'WORK', 'OTHER'].includes(email.usageType ?? '') ? email.usageType : 'PERSONAL') as 'PERSONAL' | 'WORK' | 'OTHER',
    })),
  }));
}

function contactScopeDraft(profile: WorkerPrivateProfile, scope: ContactScope): ContactScopeDraft {
  const emails = scope === 'HOME' ? profile.homeEmails ?? [] : profile.workEmails ?? [];
  const phoneItems = scope === 'HOME' ? profile.homePhones ?? [] : profile.workPhones ?? [];
  const addresses = scope === 'HOME' ? profile.homeAddresses ?? [] : [];
  const selectedEmail = primaryItem(emails);
  const selectedAddress = primaryItem(addresses);
  return {
    email: selectedEmail?.emailAddress ?? '',
    emailPublic: selectedEmail?.public === true,
    phones: phoneDrafts(scope, phoneItems),
    address: selectedAddress?.addressLines?.join(' ') ?? '',
    city: selectedAddress?.city ?? '',
    region: selectedAddress?.region ?? '',
    postalCode: selectedAddress?.postalCode ?? '',
    emergencyContacts: scope === 'HOME' ? emergencyContactDrafts(profile) : [],
    emailDirty: false,
    phonesDirty: false,
    addressDirty: false,
    emergencyContactsDirty: false,
  };
}

const contactScopes: ContactScope[] = ['HOME', 'WORK'];

function contactScopeLabel(scope: ContactScope) {
  return scope === 'HOME' ? '개인 연락처' : '회사 연락처';
}

function draftHasChanges(draft: ContactScopeDraft) {
  return draft.emailDirty || draft.phonesDirty || draft.addressDirty || draft.emergencyContactsDirty;
}

function cleanDraft(draft: ContactScopeDraft): ContactScopeDraft {
  return {
    ...draft,
    emailDirty: false,
    phonesDirty: false,
    addressDirty: false,
    emergencyContactsDirty: false,
  };
}

export function ContactChangeDialog({
  open,
  initialSection,
  port,
  worker,
  profile,
  fixtureMode,
  onClose,
  onSubmitted,
}: ContactChangeDialogProps) {
  const canEditHome = profile.canReadHomeContactFull && profile.homeContactLoaded;
  const canEditWork = profile.canReadWorkContactFull && profile.workContactLoaded;
  const sectionIdPrefix = useId();
  const [sectionRequest, setSectionRequest] = useState<{ section: ContactSection }>(() => ({ section: initialSection ?? 'HOME' }));
  const activeSection = sectionRequest.section;
  const sectionRefs = useRef<Record<ContactSection, HTMLElement | null>>({ HOME: null, WORK: null, EMERGENCY: null });
  const focusSection = useCallback((section: ContactSection) => {
    setSectionRequest({ section });
  }, []);

  useEffect(() => {
    if (open) focusSection(initialSection ?? 'HOME');
  }, [open, initialSection, worker.personId, focusSection]);

  useEffect(() => {
    if (!open) return;
    // 선택 영역의 숨김이 실제 DOM에서 해제된 뒤 초점을 옮깁니다. 같은 영역을 다시 눌러도 이동합니다.
    const target = sectionRefs.current[sectionRequest.section];
    target?.focus({ preventScroll: true });
    target?.scrollIntoView({ block: 'nearest' });
  }, [open, sectionRequest]);

  const [effectiveDate, setEffectiveDate] = useState(todayInSeoul());
  const effectiveDateInputRef = useRef<HTMLInputElement>(null);
  const effectiveDateError = effectiveDate ? undefined : '시행일을 입력해 주세요.';
  const [drafts, setDrafts] = useState<Record<ContactScope, ContactScopeDraft>>(() => ({
    HOME: contactScopeDraft(profile, 'HOME'),
    WORK: contactScopeDraft(profile, 'WORK'),
  }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const operationsByScope = useRef<Partial<Record<ContactScope, ContactSubmissionOperation>>>({});

  useEffect(() => {
    if (!open) return;
    setDrafts({
      HOME: contactScopeDraft(profile, 'HOME'),
      WORK: contactScopeDraft(profile, 'WORK'),
    });
    operationsByScope.current = {};
    setEffectiveDate(todayInSeoul());
    setError(undefined);
  }, [open, worker.personId, profile]);

  const updateDraft = (scope: ContactScope, updater: (draft: ContactScopeDraft) => ContactScopeDraft) => {
    setDrafts((current) => ({ ...current, [scope]: updater(current[scope]) }));
    setError(undefined);
  };

  const updatePhone = (scope: ContactScope, index: number, update: Partial<ContactPhoneDraft>) => {
    updateDraft(scope, (draft) => ({
      ...draft,
      phones: draft.phones.map((phone, itemIndex) => itemIndex === index ? { ...phone, ...update } : phone),
      phonesDirty: true,
    }));
  };

  const makePrimary = (scope: ContactScope, index: number) => {
    updateDraft(scope, (draft) => ({
      ...draft,
      phones: draft.phones.map((phone, itemIndex) => ({ ...phone, primary: itemIndex === index })),
      phonesDirty: true,
    }));
  };

  const removePhone = (scope: ContactScope, index: number) => {
    updateDraft(scope, (draft) => ({
      ...draft,
      phones: normalizePrimary(draft.phones.filter((_, itemIndex) => itemIndex !== index)),
      phonesDirty: true,
    }));
  };

  const addPhone = (scope: ContactScope) => {
    updateDraft(scope, (draft) => ({
      ...draft,
      phones: [...draft.phones, {
        key: crypto.randomUUID(),
        phoneNumber: '',
        extension: '',
        usageType: scope === 'HOME' ? 'MOBILE' : 'WORK',
        primary: draft.phones.length === 0,
        public: false,
      }],
      phonesDirty: true,
    }));
  };

  const updateEmergencyContact = (contactIndex: number, update: Partial<EmergencyContactDraft>) => {
    updateDraft('HOME', (draft) => ({
      ...draft,
      emergencyContacts: draft.emergencyContacts.map((contact, index) => index === contactIndex ? { ...contact, ...update } : contact),
      emergencyContactsDirty: true,
    }));
  };

  const addEmergencyContact = () => {
    updateDraft('HOME', (draft) => draft.emergencyContacts.length >= maxEmergencyContacts ? draft : ({
      ...draft,
      emergencyContacts: [...draft.emergencyContacts, {
        key: crypto.randomUUID(),
        name: '',
        relationshipType: 'OTHER',
        phoneNumbers: [{ key: crypto.randomUUID(), phoneNumber: '', extension: '', usageType: 'MOBILE', primary: true, public: false }],
        emailAddresses: [],
      }],
      emergencyContactsDirty: true,
    }));
  };

  const removeEmergencyContact = (contactIndex: number) => {
    updateDraft('HOME', (draft) => ({
      ...draft,
      emergencyContacts: draft.emergencyContacts.filter((_, index) => index !== contactIndex),
      emergencyContactsDirty: true,
    }));
  };

  const updateEmergencyPhone = (contactIndex: number, phoneIndex: number, update: Partial<ContactPhoneDraft>) => {
    updateDraft('HOME', (draft) => ({
      ...draft,
      emergencyContacts: draft.emergencyContacts.map((contact, index) => index === contactIndex ? {
        ...contact,
        phoneNumbers: contact.phoneNumbers.map((phone, itemIndex) => itemIndex === phoneIndex ? { ...phone, ...update } : phone),
      } : contact),
      emergencyContactsDirty: true,
    }));
  };

  const addEmergencyPhone = (contactIndex: number) => {
    updateDraft('HOME', (draft) => ({
      ...draft,
      emergencyContacts: draft.emergencyContacts.map((contact, index) => index === contactIndex ? {
        ...contact,
        phoneNumbers: contact.phoneNumbers.length >= maxEmergencyContactMethods ? contact.phoneNumbers : [...contact.phoneNumbers, {
          key: crypto.randomUUID(), phoneNumber: '', extension: '', usageType: 'MOBILE',
          primary: contact.phoneNumbers.length === 0, public: false,
        }],
      } : contact),
      emergencyContactsDirty: true,
    }));
  };

  const removeEmergencyPhone = (contactIndex: number, phoneIndex: number) => {
    updateDraft('HOME', (draft) => ({
      ...draft,
      emergencyContacts: draft.emergencyContacts.map((contact, index) => index === contactIndex ? {
        ...contact,
        phoneNumbers: normalizePrimary(contact.phoneNumbers.filter((_, itemIndex) => itemIndex !== phoneIndex)),
      } : contact),
      emergencyContactsDirty: true,
    }));
  };

  const updateEmergencyEmail = (contactIndex: number, emailIndex: number,
    update: Partial<EmergencyContactDraft['emailAddresses'][number]>) => {
    updateDraft('HOME', (draft) => ({
      ...draft,
      emergencyContacts: draft.emergencyContacts.map((contact, index) => index === contactIndex ? {
        ...contact,
        emailAddresses: contact.emailAddresses.map((email, itemIndex) => itemIndex === emailIndex ? { ...email, ...update } : email),
      } : contact),
      emergencyContactsDirty: true,
    }));
  };

  const addEmergencyEmail = (contactIndex: number) => {
    updateDraft('HOME', (draft) => ({
      ...draft,
      emergencyContacts: draft.emergencyContacts.map((contact, index) => index === contactIndex ? {
        ...contact,
        emailAddresses: contact.emailAddresses.length >= maxEmergencyContactMethods
          ? contact.emailAddresses
          : [...contact.emailAddresses, { key: crypto.randomUUID(), emailAddress: '', usageType: 'PERSONAL' }],
      } : contact),
      emergencyContactsDirty: true,
    }));
  };

  const removeEmergencyEmail = (contactIndex: number, emailIndex: number) => {
    updateDraft('HOME', (draft) => ({
      ...draft,
      emergencyContacts: draft.emergencyContacts.map((contact, index) => index === contactIndex ? {
        ...contact,
        emailAddresses: contact.emailAddresses.filter((_, itemIndex) => itemIndex !== emailIndex),
      } : contact),
      emergencyContactsDirty: true,
    }));
  };

  const changedScopes = useMemo(() => contactScopes.filter((scope) => {
    const canEdit = scope === 'HOME' ? canEditHome : canEditWork;
    return canEdit && draftHasChanges(drafts[scope]);
  }), [canEditHome, canEditWork, drafts]);

  const prepareInput = (scope: ContactScope): { input?: ContactChangeInput; error?: string } => {
    const draft = drafts[scope];
    const scopeEmails = scope === 'HOME' ? profile.homeEmails ?? [] : profile.workEmails ?? [];
    const scopeAddresses = scope === 'HOME' ? profile.homeAddresses ?? [] : [];
    const emailValue = draft.email.trim();
    if (draft.emailDirty && emailValue && (emailValue.length > 320 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue))) {
      return { error: '이메일 형식을 확인해 주세요.' };
    }
    const phoneRows = draft.phones.map((phone) => ({ ...phone, phoneNumber: phone.phoneNumber.trim(), extension: phone.extension.trim() }));
    if (draft.phonesDirty && phoneRows.some((phone) => !phone.phoneNumber || !/^[0-9+(). -]{4,50}$/.test(phone.phoneNumber) || !/\d/.test(phone.phoneNumber))) {
      return { error: '각 전화번호는 숫자를 하나 이상 포함해야 하며 + ( ) . - 공백만 사용할 수 있습니다.' };
    }
    const addressValue = draft.address.trim();
    if (draft.addressDirty && !addressValue && (draft.city.trim() || draft.region.trim() || draft.postalCode.trim())) {
      return { error: '시·도, 시·군·구 또는 우편번호를 입력하려면 주소도 입력해 주세요.' };
    }
    if (draft.addressDirty && draft.postalCode.trim() && !/^\d{5}$/.test(draft.postalCode.trim())) {
      return { error: '우편번호는 숫자 5자리로 입력해 주세요.' };
    }
    const emergencyContactRows = draft.emergencyContacts.map((contact) => ({
      ...contact,
      name: contact.name.trim(),
      phoneNumbers: contact.phoneNumbers.map((phone) => ({ ...phone, phoneNumber: phone.phoneNumber.trim() })),
      emailAddresses: contact.emailAddresses.map((emailAddress) => ({ ...emailAddress, emailAddress: emailAddress.emailAddress.trim().toLowerCase() })),
    }));
    if (draft.emergencyContactsDirty && emergencyContactRows.some((contact) => !contact.name || (!contact.phoneNumbers.length && !contact.emailAddresses.length))) {
      return { error: '비상 연락처마다 이름과 전화번호 또는 이메일을 하나 이상 입력해 주세요.' };
    }
    if (draft.emergencyContactsDirty && emergencyContactRows.some((contact) => contact.phoneNumbers.some((phone) =>
      !phone.phoneNumber || !/^[0-9+(). -]{4,50}$/.test(phone.phoneNumber) || !/\d/.test(phone.phoneNumber)))) {
      return { error: '비상 연락처 전화번호 형식을 확인해 주세요.' };
    }
    if (draft.emergencyContactsDirty && emergencyContactRows.some((contact) => contact.emailAddresses.some((emailAddress) =>
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress.emailAddress)))) {
      return { error: '비상 연락처 이메일 형식을 확인해 주세요.' };
    }

    return { input: {
      scope,
      effectiveDate,
      worker: scope === 'WORK' ? { id: worker.id, descriptor: worker.name } : undefined,
      emails: draft.emailDirty ? emailPayload(scopeEmails, emailValue, scope, draft.emailPublic) : undefined,
      phones: draft.phonesDirty ? normalizePrimary(phoneRows).map((phone) => ({
        phoneNumber: phone.phoneNumber,
        extension: scope === 'WORK' ? phone.extension || undefined : undefined,
        usageType: phone.usageType,
        primary: phone.primary,
        public: phone.public,
      })) : undefined,
      addresses: scope === 'HOME' && draft.addressDirty ? addressPayload(scopeAddresses, {
        line: addressValue,
        city: draft.city.trim(),
        region: draft.region.trim(),
        postalCode: draft.postalCode.trim(),
      }) : undefined,
      emergencyContacts: scope === 'HOME' && draft.emergencyContactsDirty ? emergencyContactRows.map((contact) => ({
        name: contact.name,
        relationshipType: contact.relationshipType,
        phoneNumbers: normalizePrimary(contact.phoneNumbers).map((phone) => ({
          phoneNumber: phone.phoneNumber,
          usageType: phone.usageType as 'HOME' | 'MOBILE' | 'WORK' | 'OTHER',
          primary: phone.primary,
        })),
        emailAddresses: contact.emailAddresses.map((emailAddress, index) => ({
          emailAddress: emailAddress.emailAddress,
          usageType: emailAddress.usageType,
          primary: index === 0,
        })),
      })) : undefined,
    } };
  };

  const submitScope = async (scope: ContactScope, input: ContactChangeInput) => {
    const signature = JSON.stringify(input);
    let operation = operationsByScope.current[scope];
    if (!operation || operation.signature !== signature) {
      operation = { signature, createKey: crypto.randomUUID(), submitKey: crypto.randomUUID() };
      operationsByScope.current[scope] = operation;
    }
    const currentOperation = operation;
    const draftRequest = currentOperation.draft
      ? Promise.resolve(currentOperation.draft)
      : port.createContactChange(worker.personId, input, currentOperation.createKey).then((draft) => {
        currentOperation.draft = { id: draft.id, scope: draft.scope };
        return currentOperation.draft;
      });
    const change = await draftRequest;
    const submitted = await port.submitContactChange(change, currentOperation.submitKey);
    delete operationsByScope.current[scope];
    return submitted;
  };

  const submit = async () => {
    if (!effectiveDate) {
      effectiveDateInputRef.current?.focus();
      return;
    }
    if (!changedScopes.length) {
      setError('변경할 이메일, 전화번호, 주소 또는 비상 연락처를 수정해 주세요.');
      return;
    }
    const prepared = changedScopes.map((scope) => ({ scope, ...prepareInput(scope) }));
    const invalid = prepared.find((item) => item.error || !item.input);
    if (invalid) {
      focusSection(invalid.scope === 'HOME' && invalid.error?.includes('비상') ? 'EMERGENCY' : invalid.scope);
      setError(`${contactScopeLabel(invalid.scope)}: ${invalid.error ?? '변경 내용을 확인해 주세요.'}`);
      return;
    }
    const successMessage = changedScopes.length === 2
      ? `${worker.name}님의 개인·회사 연락처 변경을 각각 승인 요청했습니다.`
      : `${worker.name}님의 ${contactScopeLabel(changedScopes[0])} 변경을 승인 요청했습니다.`;
    if (fixtureMode) {
      onSubmitted(successMessage, []);
      return;
    }
    setSubmitting(true);
    setError(undefined);
    const results = await Promise.allSettled(prepared.map((item) => submitScope(item.scope, item.input!)));
    const succeeded = prepared.flatMap((item, index) => results[index].status === 'fulfilled' ? [item.scope] : []);
    const failed = prepared.flatMap((item, index) => results[index].status === 'rejected' ? [item.scope] : []);
    if (succeeded.length) {
      setDrafts((current) => ({
        HOME: succeeded.includes('HOME') ? cleanDraft(current.HOME) : current.HOME,
        WORK: succeeded.includes('WORK') ? cleanDraft(current.WORK) : current.WORK,
      }));
    }
    if (!failed.length) {
      const workflows = prepared.flatMap((item, index) => {
        const result = results[index];
        return result.status === 'fulfilled' && result.value.workflow
          ? [{ scope: item.scope, workflow: result.value.workflow }]
          : [];
      });
      onSubmitted(successMessage, workflows);
    } else {
      const firstFailure = results.find((result) => result.status === 'rejected');
      const reason = firstFailure?.status === 'rejected' && firstFailure.reason instanceof Error
        ? firstFailure.reason.message
        : '연락처 변경을 제출하지 못했습니다.';
      const completedPrefix = succeeded.length
        ? `${succeeded.map(contactScopeLabel).join('·')} 변경은 승인 요청했습니다. `
        : '';
      setError(`${completedPrefix}${failed.map(contactScopeLabel).join('·')} 변경 요청에 실패했습니다. ${reason}`);
    }
    setSubmitting(false);
  };

  const renderContactSection = (scope: ContactScope) => {
    const draft = drafts[scope];
    const canEdit = scope === 'HOME' ? canEditHome : canEditWork;
    const phoneTypes = scope === 'HOME' ? homePhoneTypes : workPhoneTypes;
    const scopeName = scope === 'HOME' ? '개인' : '회사';
    const phoneSummary = draft.phones.length ? `${scopeName} 전화번호 ${draft.phones.length}개` : `${scopeName} 전화번호 없음`;
    return <Box
      key={scope}
      component="section"
      id={`${sectionIdPrefix}-${scope}`}
      ref={(element: HTMLElement | null) => { sectionRefs.current[scope] = element; }}
      tabIndex={-1}
      aria-label={`${contactScopeLabel(scope)} 입력`}
      data-testid={`contact-${scope.toLowerCase()}-section`}
      sx={{
        '&:focus-visible': createFocusRingStyles({ inset: true }),
        display: activeSection === scope ? 'grid' : 'none',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '132px minmax(0, 1fr)' },
        columnGap: 3,
        rowGap: { xs: 1.5, sm: 0 },
        p: { xs: 1.5, sm: 2 },
        border: `1px solid ${designTokens.color.divider}`,
        borderRadius: designTokens.radius.surface,
      }}
    >
      <Box>
        <Typography variant="subtitle2">{contactScopeLabel(scope)}</Typography>
        <Typography variant="caption" color="text.secondary">
          이메일·전화번호{scope === 'HOME' ? '·주소' : ''}
        </Typography>
      </Box>
      {!canEdit ? <Alert severity="warning">원문을 불러올 수 없어 이 연락처는 수정할 수 없습니다.</Alert> : <Stack spacing={2.25} sx={{ minWidth: 0 }}>
        {scope === 'WORK' && <Typography variant="body2" color="text.secondary">
          대표 회사 이메일과 대표 업무 전화·업무용 휴대전화 중 공개한 항목만 조직도 프로필에 표시됩니다. 비공개 연락처는 조회 권한이 있는 사용자만 확인할 수 있습니다. 개인 연락처·집 주소·비상 연락처는 조직도에 표시하지 않습니다.
        </Typography>}
        <FormTextField
          type="email"
          label={`${scopeName} 이메일`}
          value={draft.email}
          onChange={(event) => updateDraft(scope, (current) => ({ ...current, email: event.target.value, emailDirty: true }))}
        />
        {scope === 'WORK' && <ChoiceControl
          kind="switch"
          label="회사 이메일 공개"
          ariaLabel="회사 이메일 공개"
          checked={draft.emailPublic}
          disabled={!draft.email.trim()}
          onCheckedChange={(checked) => updateDraft(scope, (current) => ({ ...current, emailPublic: checked, emailDirty: true }))}
          sx={{ alignSelf: 'flex-start' }}
        />}

        <Box component="section" aria-label={phoneSummary}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 650 }}>
            전화번호
          </Typography>
          <Stack spacing={1}>
            {draft.phones.map((phone, index) => <Box
              key={phone.key}
              data-testid={`contact-${scope.toLowerCase()}-phone-row-${index}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr) auto auto',
                  sm: 'minmax(0, 1fr) minmax(136px, 168px) auto auto',
                },
                gap: 1,
                alignItems: 'center',
                minWidth: 0,
              }}
            >
              <FormTextField
                type="tel"
                label={`${scopeName} 전화번호 ${index + 1}`}
                value={phone.phoneNumber}
                onChange={(event) => updatePhone(scope, index, { phoneNumber: event.target.value })}
                sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, minWidth: 0 }}
              />
              <FormTextField
                select
                label={`${scopeName} 전화번호 ${index + 1} 유형`}
                value={phone.usageType}
                onChange={(event) => updatePhone(scope, index, { usageType: event.target.value as ContactPhoneUsage })}
                sx={{ minWidth: 0 }}
              >
                {phoneTypes.map((type) => <MenuItem key={type.value} value={type.value}><ValueBadge label={type.label} /></MenuItem>)}
              </FormTextField>
              <ChoiceControl
                sx={{ whiteSpace: 'nowrap' }}
                kind="radio"
                size="small"
                checked={phone.primary}
                onCheckedChange={() => makePrimary(scope, index)}
                label="대표"
              />
              <IconButton
                aria-label={`${scopeName} ${index + 1}번째 전화번호 삭제`}
                onClick={() => removePhone(scope, index)}
                sx={{ width: 40, height: 40 }}
              >
                <DeleteOutlined fontSize="small" />
              </IconButton>
              {scope === 'WORK' && <Box sx={{ gridColumn: '1 / -1' }}>
                <ChoiceControl
                  kind="switch"
                  label="전화번호 공개"
                  ariaLabel={`회사 전화번호 ${index + 1} 공개`}
                  checked={phone.public}
                  disabled={!phone.phoneNumber.trim()}
                  onCheckedChange={(checked) => updatePhone(scope, index, { public: checked })}
                />
                {(!['WORK', 'MOBILE'].includes(phone.usageType) || !phone.primary) && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  {!['WORK', 'MOBILE'].includes(phone.usageType)
                    ? '팩스·기타 번호는 조직도에 표시하지 않습니다.'
                    : '공개 설정은 저장되며, 대표 번호로 지정하면 조직도에 표시됩니다.'}
                </Typography>}
              </Box>}
            </Box>)}
            {!draft.phones.length && <Typography variant="body2" color="text.secondary">등록된 전화번호가 없습니다.</Typography>}
            <Button size="small" startIcon={<AddOutlined />} onClick={() => addPhone(scope)} sx={{ alignSelf: 'flex-start' }}>{scopeName} 전화번호 추가</Button>
          </Stack>
        </Box>

        {scope === 'HOME' ? <Stack spacing={1}>
          <FormTextField label="집 주소" value={draft.address} onChange={(event) => updateDraft('HOME', (current) => ({ ...current, address: event.target.value, addressDirty: true }))} placeholder="도로명, 건물명, 상세 주소" />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ '& > *': { flex: 1, minWidth: 0 } }}>
            <FormTextField label="시·군·구" value={draft.city} onChange={(event) => updateDraft('HOME', (current) => ({ ...current, city: event.target.value, addressDirty: true }))} />
            <FormTextField label="시·도" value={draft.region} onChange={(event) => updateDraft('HOME', (current) => ({ ...current, region: event.target.value, addressDirty: true }))} />
            <FormTextField label="우편번호" value={draft.postalCode} onChange={(event) => updateDraft('HOME', (current) => ({ ...current, postalCode: event.target.value, addressDirty: true }))} />
          </Stack>
        </Stack> : <Typography variant="caption" color="text.secondary">회사 주소는 공유 Location 정보이므로 조직·근무지 관리에서 변경합니다.</Typography>}
      </Stack>}
    </Box>;
  };

  const emergencyContacts = drafts.HOME.emergencyContacts;

  return <FormDialog
    open={open}
    onEntered={() => {
      const target = sectionRefs.current[activeSection];
      // 열리는 동안 선택한 영역이나 입력 초점을 덮어쓰지 않고 표면의 초기 초점만 이동합니다.
      if (target && target.ownerDocument.activeElement === target.closest('[role="dialog"]')) {
        target.focus({ preventScroll: true });
        target.scrollIntoView({ block: 'nearest' });
      }
    }}
    onClose={onClose}
    onSubmit={submit}
    title="연락처 변경"
    help="연락처와 공개 여부는 최종 승인 후 시행일에 반영됩니다. 개인 연락처와 회사 연락처는 별도로 관리하며, 변경한 섹션마다 승인 요청이 생성됩니다."
    headerContent={<Stack component="nav" aria-label="연락처 영역 이동" direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
      {(['HOME', 'WORK'] as const).map((scope) => <Button
        key={scope}
        size="small"
        variant="outlined"
        sx={{ bgcolor: activeSection === scope ? designTokens.color.actionSoft : undefined }}
        aria-controls={`${sectionIdPrefix}-${scope}`}
        disabled={submitting}
        aria-pressed={activeSection === scope}
        onClick={() => focusSection(scope)}
      >{contactScopeLabel(scope)}</Button>)}
      {canEditHome && <Button
        size="small"
        variant="outlined"
        sx={{ bgcolor: activeSection === 'EMERGENCY' ? designTokens.color.actionSoft : undefined }}
        aria-controls={`${sectionIdPrefix}-EMERGENCY`}
        disabled={submitting}
        aria-pressed={activeSection === 'EMERGENCY'}
        onClick={() => focusSection('EMERGENCY')}
      >비상 연락처</Button>}
    </Stack>}
    cancelLabel="취소"
    submitLabel="승인 요청"
    busy={submitting}
    busyLabel="제출 중"
    submitDisabled={!changedScopes.length || Boolean(effectiveDateError)}
    maxWidth="md"
  >
    {error && <Alert severity="error">{error}</Alert>}
    <Typography variant="body2">{worker.name} · {worker.workerNumber}</Typography>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'flex-end' }, justifyContent: 'space-between' }}>
      <FormTextField
        required
        type="date"
        label="시행일"
        inputRef={effectiveDateInputRef}
        value={effectiveDate}
        error={Boolean(effectiveDateError)}
        helperText={effectiveDateError}
        onChange={(event) => { setEffectiveDate(event.target.value); setError(undefined); }}
        sx={{ width: { sm: 220 }, flexShrink: 0 }}
      />
    </Stack>

    {changedScopes.length > 0 && <Alert severity="info">
      {changedScopes.map(contactScopeLabel).join(' · ')} 변경 {changedScopes.length}건을 승인 요청합니다.
      최종 승인 후 {effectiveDate || '선택한 시행일'}에 반영됩니다. 다른 영역으로 이동해도 입력한 내용은 유지됩니다.
    </Alert>}
    {renderContactSection('HOME')}
    {renderContactSection('WORK')}

    {canEditHome && <Box
      component="section"
      id={`${sectionIdPrefix}-EMERGENCY`}
      ref={(element: HTMLElement | null) => { sectionRefs.current.EMERGENCY = element; }}
      tabIndex={-1}
      aria-label={`비상 연락처 ${emergencyContacts.length}명`}
      sx={{
        '&:focus-visible': createFocusRingStyles({ inset: true }),
        display: activeSection === 'EMERGENCY' ? 'grid' : 'none',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '132px minmax(0, 1fr)' },
        columnGap: 3,
        rowGap: { xs: 1.5, sm: 0 },
        p: { xs: 1.5, sm: 2 },
        border: `1px solid ${designTokens.color.divider}`,
        borderRadius: designTokens.radius.surface,
      }}
    >
      <Box>
        <Typography variant="subtitle2">비상 연락처</Typography>
        <Typography variant="caption" color="text.secondary">개인 연락처와 별도 관리</Typography>
      </Box>
      {!profile.emergencyContactsLoaded ? <Alert severity="warning">비상 연락처를 불러오지 못해 이 항목은 수정할 수 없습니다.</Alert> : <Stack spacing={1.25}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">최대 {maxEmergencyContacts}명, 각 연락수단 {maxEmergencyContactMethods}개</Typography>
          <Button size="small" startIcon={<AddOutlined />} disabled={emergencyContacts.length >= maxEmergencyContacts} onClick={addEmergencyContact} sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>연락처 추가</Button>
        </Stack>
        {emergencyContacts.map((contact, contactIndex) => <Box
          key={contact.key}
          data-testid={`emergency-contact-editor-${contactIndex}`}
          sx={{ position: 'relative', p: 1.5, pt: 2, border: `1px solid ${designTokens.color.divider}`, borderRadius: designTokens.radius.surface }}
        >
          <IconButton aria-label={`${contactIndex + 1}번째 비상 연락처 삭제`} onClick={() => removeEmergencyContact(contactIndex)} sx={{ position: 'absolute', top: 4, right: 4, width: 40, height: 40 }}><DeleteOutlined fontSize="small" /></IconButton>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ pr: 4, '& > *': { flex: 1, minWidth: 0 } }}>
            <FormTextField label={`비상 연락처 ${contactIndex + 1} 이름`} value={contact.name} onChange={(event) => updateEmergencyContact(contactIndex, { name: event.target.value })} />
            <FormTextField select label={`비상 연락처 ${contactIndex + 1} 관계`} value={contact.relationshipType} onChange={(event) => updateEmergencyContact(contactIndex, { relationshipType: event.target.value })}>
              {!emergencyRelationshipTypes.some((type) => type.value === contact.relationshipType) && <MenuItem value={contact.relationshipType}><ValueBadge label={contact.relationshipType} /></MenuItem>}
              {emergencyRelationshipTypes.map((type) => <MenuItem key={type.value} value={type.value}><ValueBadge label={type.label} /></MenuItem>)}
            </FormTextField>
          </Stack>
          <Stack spacing={0.75} sx={{ mt: 1.25 }}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 650 }}>전화번호</Typography>
              <Button size="small" disabled={contact.phoneNumbers.length >= maxEmergencyContactMethods} onClick={() => addEmergencyPhone(contactIndex)}>전화 추가</Button>
            </Stack>
            {contact.phoneNumbers.map((phone, phoneIndex) => <Box key={phone.key} sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', sm: 'minmax(0, 1fr) 132px auto' },
              gap: 0.75,
              alignItems: 'center',
              minWidth: 0,
            }}>
              <FormTextField fullWidth type="tel" label={`전화번호 ${phoneIndex + 1}`} value={phone.phoneNumber} onChange={(event) => updateEmergencyPhone(contactIndex, phoneIndex, { phoneNumber: event.target.value })} sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, minWidth: 0 }} />
              <FormTextField select label={`비상 연락처 ${contactIndex + 1} 전화번호 ${phoneIndex + 1} 유형`} value={phone.usageType} onChange={(event) => updateEmergencyPhone(contactIndex, phoneIndex, { usageType: event.target.value as ContactPhoneUsage })} sx={{ minWidth: 0 }}>
                {emergencyPhoneTypes.map((type) => <MenuItem key={type.value} value={type.value}><ValueBadge label={type.label} /></MenuItem>)}
              </FormTextField>
              <IconButton aria-label={`${contactIndex + 1}번째 비상 연락처 ${phoneIndex + 1}번째 전화번호 삭제`} onClick={() => removeEmergencyPhone(contactIndex, phoneIndex)} sx={{ width: 40, height: 40 }}><DeleteOutlined fontSize="small" /></IconButton>
            </Box>)}
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', pt: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 650 }}>이메일</Typography>
              <Button size="small" disabled={contact.emailAddresses.length >= maxEmergencyContactMethods} onClick={() => addEmergencyEmail(contactIndex)}>이메일 추가</Button>
            </Stack>
            {contact.emailAddresses.map((emailAddress, emailIndex) => <Box key={emailAddress.key} sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', sm: 'minmax(0, 1fr) 132px auto' },
              gap: 0.75,
              alignItems: 'center',
              minWidth: 0,
            }}>
              <FormTextField fullWidth type="email" label={`이메일 ${emailIndex + 1}`} value={emailAddress.emailAddress} onChange={(event) => updateEmergencyEmail(contactIndex, emailIndex, { emailAddress: event.target.value })} sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, minWidth: 0 }} />
              <FormTextField select label={`비상 연락처 ${contactIndex + 1} 이메일 ${emailIndex + 1} 유형`} value={emailAddress.usageType} onChange={(event) => updateEmergencyEmail(contactIndex, emailIndex, { usageType: event.target.value as EmergencyContactDraft['emailAddresses'][number]['usageType'] })} sx={{ minWidth: 0 }}>
                {emergencyEmailTypes.map((type) => <MenuItem key={type.value} value={type.value}><ValueBadge label={type.label} /></MenuItem>)}
              </FormTextField>
              <IconButton aria-label={`${contactIndex + 1}번째 비상 연락처 ${emailIndex + 1}번째 이메일 삭제`} onClick={() => removeEmergencyEmail(contactIndex, emailIndex)} sx={{ width: 40, height: 40 }}><DeleteOutlined fontSize="small" /></IconButton>
            </Box>)}
          </Stack>
        </Box>)}
        {!emergencyContacts.length && <Box sx={{ px: 1.5, py: 2, border: `1px dashed ${designTokens.color.strongDivider}`, borderRadius: designTokens.radius.surface }}>
          <Typography variant="body2" color="text.secondary">등록된 비상 연락처가 없습니다.</Typography>
        </Box>}
      </Stack>}
    </Box>}
  </FormDialog>;
}
